import { get, assign, pick, instantiate } from "@/utils/object";

import { BeeModelManager } from "./BeeModelManager";
import { BeeModelCollection } from "./BeeModelCollection";
import { parseISO } from "@/utils/datetime";
import type { ManyOfRelatedDTO, ModelDTO } from "@/store/api/dto/model.dto";
import type { NumberString } from "@/store/api/dto/options";

export class BeeModel<Model extends ModelDTO = ModelDTO> {
  static dates: Array<string> = [];
  static times: Array<string> = [];

  declare name: string;
  id!: NumberString;
  rawId!: number | string;
  modelManager!: BeeModelManager;
  related: Model["related"] = {};
  presented: Model["presented"] = {};
  options = {} as BeeModelManager["options"];
  // relatedEntities;

  onClone?(): void;

  static records: BeeModel[];
  static getCollectionClass() {
    return BeeModelCollection as typeof BeeModelCollection<BeeModel>;
  }

  constructor(modelManager: BeeModelManager, record: ModelDTO) {
    this.modelManager = modelManager;
    this.options = this.modelManager.options;
    assign(this, pick(record, ["presented", "related"]));
    assign(this, record.attributes);

    this.rawId = this.id;
    this.id = String(this.id) as `${number}`;
  }

  clone() {
    const clonedModel = assign(instantiate(Object.getPrototypeOf(this)), this);
    clonedModel.onClone?.();

    return clonedModel as typeof this;
  }

  hasManyRelated(relationshipName: string) {
    if (!this.related[relationshipName]) return false;

    const { value } = this.related[relationshipName];
    return !!(value?.length > 0);
  }

  hasOne<T extends BeeModel = BeeModel>(relationshipName: string): T | null {
    const { table: modelType, value: relatedId } =
      this.related[relationshipName] || {};
    if (!relatedId) return null;
    const rawRecords = this.modelManager.getRecordsMap(modelType);
    const rawRecord = rawRecords[relatedId];
    const ModelClass = this.modelManager.getModelClass(modelType);
    return new ModelClass(this.modelManager, rawRecord) as T;
  }

  hasMany<T extends BeeModelCollection = BeeModelCollection>(
    relationshipName: string,
  ): T {
    const { table: modelType, value: relatedIds = [] } = (this.related[
      relationshipName
    ] ?? {}) as ManyOfRelatedDTO;

    const rawRecords = relatedIds.map(
      (id) => this.modelManager.getRecordsMap(modelType)[id],
    );

    return this.modelManager.constructCollection(rawRecords, modelType) as T;
  }

  belongsToOne<T extends BeeModelCollection>(
    modelType: string,
    relationshipName = modelType,
  ) {
    const rawRecords = this.modelManager
      .getRawRecords(modelType)
      .filter((m) => m.related[relationshipName].value == this.id);
    return this.modelManager.constructCollection(rawRecords, modelType) as T;
  }

  belongsToMany<T extends BeeModelCollection = BeeModelCollection>(
    modelType: string,
    relationshipName = modelType,
  ) {
    const rawRecords = this.modelManager
      .getRawRecords(modelType)
      .filter((m) => m.related[relationshipName].value.includes(+this.id));
    return this.modelManager.constructCollection(rawRecords, modelType) as T;
  }

  getRaw(key: string) {
    return get(this, key);
  }

  get<T>(key: string, defaultValue?: never): T | null;
  get<T>(key: string, defaultValue: T): T;
  get<T>(key: string, defaultValue?: T) {
    const value = this.getRaw(key);

    const resolvedDefaultValue = defaultValue ?? null;

    if ((this.constructor as typeof BeeModel).dates.includes(key)) {
      return value ? (parseISO(value.date) as T) : resolvedDefaultValue;
    }

    if ((this.constructor as typeof BeeModel).times.includes(key)) {
      return value
        ? (parseISO(`1970-01-01 ${value}`) as T)
        : resolvedDefaultValue;
    }

    return (value as T) ?? resolvedDefaultValue;
  }
}
